import { CenteredSpinner, Col, Flex, Row } from '@wyz/ui';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { SEARCH_FIELDS, useOnSuccessQuery } from '@wyz/utils';
import { isNil, sortBy } from 'lodash-es';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageCustomers, useManageParameters } from '~/common/api';
import { CheckboxGroupField, SelectField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import {
  DimensionField,
  PurchaseTyreAutocomplete,
  SeasonSelector,
} from '~/purchase/components';
import { COMPLETE_WHEEL_VEHICLE_TYPE } from '~/purchase/helpers';
import { SearchMode, useDimensionAttr, useSearchMode } from '~/purchase/hooks';
import {
  IAutocompleteParameterQuery,
  IBaseSearchFormSchema,
  IDimensionFields,
  ISearchTypeArgs,
} from '~/purchase/types';
// eslint-disable-next-line @wyz/module/no-relative-import
import VehicleTypeGroupToggle from './vehicle-type-group-toggle';

// eslint-disable-next-line @wyz/module/no-relative-import
import './search-bar.scss';

const DIMENSION_ORDER = [
  { field: 'width', defaultValue: undefined },
  { field: 'height', defaultValue: undefined },
  { field: 'diameter', defaultValue: undefined },
  { field: 'loadIndexes', defaultValue: [] as Array<number> },
  { field: 'speedIndexes', defaultValue: [] as Array<string> },
] as const;

type SearchBarProps = {
  customer: IUserCustomer;
  initialMode?: SearchMode;
  searchType: ISearchTypeArgs['searchType'];
};

function SearchBar({ customer, initialMode, searchType }: SearchBarProps) {
  const { t } = useI18n();
  const methods = useFormContext<IBaseSearchFormSchema>();
  const watchedValues = useWatch({
    control: methods.control,
  }) as IBaseSearchFormSchema;
  const watchedRawIdentifier = watchedValues.rawIdentifier;
  const watchedIdentifierValue = watchedValues.identifier?.value;
  const dimensionQuery: IAutocompleteParameterQuery = {
    vehicleTypes: watchedValues.vehicleTypes,
    searchType,
  };

  const { isQuickSearch } = useSearchMode({
    rawIdentifier: watchedRawIdentifier,
    identifierValue: watchedIdentifierValue,
    onSwitchToManual: () => {
      methods.setValue('fieldNameSearch', null);
      methods.setValue('identifier', null);
    },
    initialMode,
  });

  const manageParameters = useManageParameters({
    getDimensions: {
      args: {
        vehicleTypes: dimensionQuery.vehicleTypes,
        customerId: customer.id,
      },
    },
    getSeasons: {
      enabled: true,
    },
  });
  const manageCustomers = useManageCustomers({
    getBrands: {
      enabled: true,
      args: {
        customerPublicKey: customer.id,
        ctx: searchType,
      },
    },
  });

  const attrs = useDimensionAttr({
    selected: watchedValues,
    customerId: customer.id,
    ...dimensionQuery,
  });

  const onResetDeps = (updatedField: IDimensionFields) => {
    let startReset = false;
    methods.setValue('fieldNameSearch', null);
    methods.setValue('identifier', null);
    // do the resetting if necessary
    DIMENSION_ORDER.forEach(({ field, defaultValue }) => {
      if (startReset) {
        methods.resetField(field, { defaultValue });
      }
      // will start reset from the next field
      if (updatedField === field) {
        startReset = true;
      }
    });
  };

  useOnSuccessQuery(manageCustomers.getBrands, (response) => {
    if (initialMode) {
      // the user already made explicit choice => do not override
      return;
    }
    methods.setValue(
      'brands',
      response.data.filter((b) => b.recommended).map((b) => b.publicKey),
    );
  });

  return (
    <>
      <Row>
        <Col lg={12} md={12}>
          <VehicleTypeGroupToggle
            groups={(customer.settingTemplate?.vehicleTypeGroups ?? []).filter(
              (g) =>
                customer.level < CUSTOMER_LEVEL.N2 ||
                g.name !== COMPLETE_WHEEL_VEHICLE_TYPE,
            )}
            activeGroup={watchedValues.vehicleTypeGroup}
            onSelectGroup={(group) => {
              methods.reset({
                ...methods.formState.defaultValues,
                vehicleTypeGroup: group.name,
                vehicleTypes: group.vehicleTypeIds,
              });
            }}
          />
        </Col>
        <Col lg={4} md={6}>
          <PurchaseTyreAutocomplete
            dimensionQuery={dimensionQuery}
            vehicleTypeGroup={watchedValues.vehicleTypeGroup}
            availableSeasons={manageParameters.getSeasons.data?.data || []}
          />
        </Col>
        <Col lg={4} md={6}>
          <SeasonSelector
            seasons={watchedValues.seasons ?? []}
            availableSeasons={manageParameters.getSeasons.data?.data || []}
            isLoading={manageParameters.getSeasons.isProcessing}
            isDisabled={
              !watchedValues.vehicleTypeGroup ||
              (isQuickSearch &&
                Boolean(watchedValues.fieldNameSearch) &&
                (
                  [SEARCH_FIELDS.CAI, SEARCH_FIELDS.EAN] as Array<
                    typeof watchedValues.fieldNameSearch
                  >
                ).includes(watchedValues.fieldNameSearch))
            }
            onChange={(newSeasons) => {
              methods.setValue('seasons', newSeasons, {
                shouldValidate: true,
              });
            }}
            errorMessage={methods.formState.errors.seasons?.message}
            required
          />
        </Col>
        <Col lg={4} md={6}>
          <SelectField<true>
            isMulti
            withCheckboxes
            name='brands'
            label={<Trans i18nKey='app.common.product.brands' />}
            isLoading={manageCustomers.getBrands.isProcessing}
            options={(() => {
              const brands = manageCustomers.getBrands.data?.data || [];
              const recommended = sortBy(
                brands.filter((b) => b.recommended),
                'name',
              );
              const others = sortBy(
                brands.filter((b) => !b.recommended),
                'name',
              );
              const groups = [];
              if (recommended.length > 0) {
                groups.push({
                  label: t('app.purchase.search_page.recommended_brands'),
                  options: recommended.map((brand) => ({
                    label: brand.name,
                    value: brand.publicKey,
                  })),
                });
              }
              if (others.length > 0) {
                groups.push({
                  label: t('app.purchase.search_page.more_brands'),
                  options: others.map((brand) => ({
                    label: brand.name,
                    value: brand.publicKey,
                  })),
                });
              }
              return groups;
            })()}
            all={{
              enabled: true,
              selectAll: t('app.common.select_all'),
              unselectAll: t('app.common.clear_all'),
            }}
            isDisabled={
              !watchedValues.vehicleTypeGroup ||
              (isQuickSearch &&
                Boolean(watchedValues.fieldNameSearch) &&
                (
                  [SEARCH_FIELDS.CAI, SEARCH_FIELDS.EAN] as Array<
                    typeof watchedValues.fieldNameSearch
                  >
                ).includes(watchedValues.fieldNameSearch))
            }
            size='sm'
            placeholder={t('app.common.all_female')}
            countMessage={(count) => (
              <Trans i18nKey='app.common.items_count' values={{ count }} />
            )}
            maxShown={1}
            isClearable
          />
        </Col>
      </Row>
      {!isQuickSearch && (
        <Row>
          {!manageParameters.getDimensions.isSuccess &&
            manageParameters.getDimensions.isFetching && (
              <Col sm={12}>
                <CenteredSpinner />
              </Col>
            )}
          <Col className='mt-lg-0 mt-md-3' xl={4} lg={12}>
            {manageParameters.getDimensions.isSuccess && (
              <DimensionField
                name='width'
                label={<Trans i18nKey='app.common.product.width' />}
                options={attrs.widths.map((item) => ({
                  label: String(item),
                  value: item,
                }))}
                onSelect={() => {
                  onResetDeps('width');
                }}
                required
              />
            )}
          </Col>
          <Col className='mt-lg-0 mt-md-3' xl={4} lg={12}>
            {manageParameters.getDimensions.isSuccess &&
              watchedValues.width && (
                <DimensionField
                  name='height'
                  label={<Trans i18nKey='app.common.product.height' />}
                  options={attrs.heights.map((item) => ({
                    label: String(item),
                    value: item,
                  }))}
                  onSelect={() => {
                    onResetDeps('height');
                  }}
                  required
                />
              )}
          </Col>
          <Col className='mt-lg-0 mt-md-3' xl={4} lg={12}>
            {manageParameters.getDimensions.isSuccess &&
              watchedValues.height && (
                <DimensionField
                  name='diameter'
                  label={<Trans i18nKey='app.common.product.diameter' />}
                  options={attrs.diameters.map((item) => ({
                    label: String(item),
                    value: item,
                  }))}
                  onSelect={() => {
                    onResetDeps('diameter');
                  }}
                  required
                />
              )}
            <Flex className='space-x-4 mt-3'>
              {manageParameters.getDimensions.isSuccess &&
                watchedValues.diameter && (
                  <CheckboxGroupField
                    name='loadIndexes'
                    label={
                      <Trans i18nKey='app.purchase.search_page.load_index' />
                    }
                    options={attrs.loadIndexes.map((item) => ({
                      label: String(item),
                      value: item,
                    }))}
                    onChangeValue={() => {
                      onResetDeps('loadIndexes');
                    }}
                    all={{
                      enabled: true,
                      label: t('app.common.all_female'),
                    }}
                  />
                )}
              {manageParameters.getDimensions.isSuccess &&
                !isNil(watchedValues.loadIndexes) &&
                watchedValues.loadIndexes.length !== 0 && (
                  <CheckboxGroupField
                    name='speedIndexes'
                    label={
                      <Trans i18nKey='app.purchase.search_page.speed_index' />
                    }
                    options={attrs.speedIndexes.map((item) => ({
                      label: String(item),
                      value: item,
                    }))}
                    all={{
                      enabled: true,
                      label: t('app.common.all_female'),
                    }}
                  />
                )}
            </Flex>
          </Col>
        </Row>
      )}
    </>
  );
}

export default SearchBar;
